import { AppNode } from "@features/builder/graph/types"
import { getParentsList } from "@utils/node/getParentsList"

type GetLockRelatedNodesArgs = {
  /** All nodes in current graph */
  allNodes: AppNode[]
  /** The node being edited */
  currNodeId: AppNode["id"] | undefined
  /**
   * How many levels of ancestors/descendants to include.
   *
   * - `0` — disabled; no related nodes are locked.
   * - `Infinity` — unlimited traversal; the entire ancestor and descendant
   *   chain is locked, but the function still returns (unlike a true infinite
   *   loop). Effectively the same as passing a depth larger than the graph.
   * - Any positive integer — locks exactly that many levels up and down.
   */
  depth?: number
}

/**
 * Returns the node IDs that should be locked together with the edited node.
 *
 * The lock propagates through the selected node's full ancestor chain and full
 * descendant chain. This includes parents, grandparents, children,
 * grandchildren, and any other nodes reachable by continuously walking upward
 * or downward from the edited node.
 *
 * It does not include sideways branches that require changing direction while
 * traversing the graph, such as siblings, cousins, or children of ancestor
 * nodes that are not also descendants of the edited node.
 */
export const getLockRelatedNodes = ({
  allNodes,
  currNodeId,
  depth = 2,
}: GetLockRelatedNodesArgs) => {
  if (!currNodeId) return []

  const { directParents, directChildren } = nodesRelationshipMapper(allNodes)

  const parentNodesIds = getDeepRelatedNodes(directParents, currNodeId, depth)
  const childrenNodesIds = getDeepRelatedNodes(directChildren, currNodeId, depth)

  return [...parentNodesIds, ...childrenNodesIds]
}

function nodesRelationshipMapper(allNodes: AppNode[]) {
  const directParents = new Map<AppNode["id"], AppNode["id"][]>()
  const directChildren = new Map<AppNode["id"], AppNode["id"][]>()

  for (const node of allNodes) {
    const parentsList = getParentsList(node)

    directParents.set(node.id, parentsList)

    for (const parentNodeId of parentsList) {
      const existingList = directChildren.get(parentNodeId) ?? []
      directChildren.set(parentNodeId, [...existingList, node.id])
    }
  }

  return { directParents, directChildren }
}

/**
 * Walks one relationship direction from a single start node.
 *
 * This keeps `getLockRelatedNodes` scoped to the edited node instead of
 * precomputing transitive relationships for the full graph.
 */
function getDeepRelatedNodes(
  relationshipMap: Map<AppNode["id"], AppNode["id"][]>,
  nodeId: AppNode["id"],
  depth: number,
) {
  const relatedNodes = new Set<AppNode["id"]>()
  let frontier = relationshipMap.get(nodeId) ?? []

  // each iteration processes one depth level; frontier holds all nodes at that level
  for (let currentDepth = 1; currentDepth <= depth && frontier.length > 0; currentDepth++) {
    const next: AppNode["id"][] = []

    for (const id of frontier) {
      if (relatedNodes.has(id)) continue
      relatedNodes.add(id)
      next.push(...(relationshipMap.get(id) ?? []))
    }

    // advance frontier to the next depth level
    frontier = next
  }

  return Array.from(relatedNodes)
}
